/**
 * This file is part of the Haiman.
 * @link     : https://haiman.io/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : Emil Zhai (root@derzh.com)
 * @copyright: Copyright (c) 2018 Hangzhou Haila Network Technology Co., Ltd.
 */
import store from '@/store';
import { COMMON } from '@/store/types';
import * as storage from '@/utils/storage';
import { RouteInfo } from '@/utils/navigation';

export interface AudioGroupInfo {
  control: boolean;
  background: boolean;
  src: string;
  muted: boolean;
  volume: number;
  playing: boolean;
  loop: boolean;
  autoPaused: boolean;
  duration: number;
  currentTime: number;
  route?: RouteInfo;
}

export interface StoreCommonAudioState {
  groups: Record<string, AudioGroupInfo>;
  players: Record<string, HTMLAudioElement>;
}

const createInfo = (group: string): AudioGroupInfo => ({
  control: true,
  background: false,
  src: '',
  muted: false,
  volume: 1,
  playing: false,
  loop: !!storage.getLocal(`audio.${group}.loop`),
  autoPaused: false,
  duration: -1,
  currentTime: 0,
});

export default {
  namespaced: true,
  state: {
    groups: {
      voice: createInfo('voice'),
      music: createInfo('music'),
    },
    players: {},
  },
  getters: {},
  actions: {},
  mutations: {
    [COMMON.AUDIO_SET_PROPS](state: StoreCommonAudioState, params) {
      const info = state.groups[params.group];
      if (!info) {
        return;
      }
      // Create player
      const player = state.players[params.group] || document.createElement('AUDIO');
      if (!state.players[params.group]) {
        player.addEventListener('timeupdate', (e: Event) => {
          const el = e.target as HTMLAudioElement;
          store.commit(`common/audio/${COMMON.AUDIO_SET_PROPS}`, {
            control: false,
            group: params.group,
            currentTime: el.currentTime,
          });
        });
        player.addEventListener('durationchange', (e: Event) => {
          const el = e.target as HTMLAudioElement;
          store.commit(`common/audio/${COMMON.AUDIO_SET_PROPS}`, {
            control: false,
            group: params.group,
            duration: el.duration,
          });
        });
        player.addEventListener('pause', () => {
          store.commit(`common/audio/${COMMON.AUDIO_SET_PROPS}`, {
            control: false,
            group: params.group,
            playing: false,
          });
        });
        player.addEventListener('ended', () => {
          store.commit(`common/audio/${COMMON.AUDIO_SET_PROPS}`, {
            control: false,
            group: params.group,
            playing: false,
            currentTime: 0,
            background: false,
          });
        });
        state.players[params.group] = player;
      }
      // Set props
      Object.keys(params).forEach((k) => {
        if (k === 'group' || info[k] === params[k]) {
          return;
        }
        if (k === 'src') {
          info.duration = -1;
          info.autoPaused = false;
        }
        info[k] = params[k];
      });
      // Apply params
      if (params.control !== false) {
        if (player.src !== info.src) {
          player.src = info.src;
        }
        const applyParams = (): void => {
          if (player.muted !== info.muted) {
            player.muted = info.muted;
          } else if (player.volume !== info.volume) {
            player.volume = info.volume;
          }
          if (params.currentTime !== void 0) {
            player.currentTime = info.currentTime;
          }
          if (player.loop !== info.loop) {
            player.loop = info.loop;
            storage.setLocal(`audio.${params.group}.loop`, info.loop);
          }
        };
        let promise;
        if (info.playing && player.paused) {
          // Pause music when listening another voice.
          if (params.group !== 'music' && state.groups.music.playing) {
            store.commit(`common/audio/${COMMON.AUDIO_SET_PROPS}`, {
              control: false,
              group: 'music',
              playing: false,
              autoPaused: true,
            });
            state.players.music.pause();
          }
          info.autoPaused = false;
          promise = player.play();
        } else if (!info.playing && !player.paused) {
          promise = player.pause();
        }
        if (typeof promise === 'object' && promise.then) {
          promise.then(applyParams);
        } else applyParams();
      }
      // Correct duration.
      if (params.currentTime > info.duration) {
        info.duration = params.currentTime;
      }
      // Resume music if paused by another voice.
      if (params.playing === false && params.group !== 'music' && state.groups.music.autoPaused) {
        const resumeAutoPause = (): void =>
          store.commit(`common/audio/${COMMON.AUDIO_SET_PROPS}`, {
            control: false,
            group: 'music',
            playing: true,
            autoPaused: false,
          });
        const p = state.players.music.play();
        if (typeof p === 'object' && p.then) {
          p.then(resumeAutoPause);
        } else resumeAutoPause();
      }
    },
  },
};
